import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Maximize } from 'lucide-react'
import ConnectionsGraph, { clearPositions } from './ConnectionsGraph'
import ConnectionsLegend from './ConnectionsLegend'
import PatentNodeDetailPanel from './PatentNodeDetailPanel'
import { getPatentConnections, discoverPatentConnections } from '../../services/api'
import { PATENT_NODE_TYPES, PATENT_EDGE_TYPES } from '../../constants/nodeTypes'
import type { ConnectionsNode } from '../../types'

const PATENT_NODE_TYPE_KEYS = Object.keys(PATENT_NODE_TYPES)
const PATENT_EDGE_TYPE_KEYS = Object.keys(PATENT_EDGE_TYPES)

interface PatentConnectionsPanelProps {
  patentId: string
  onOpenPatent: (patentId: string) => void
  onInventorSearch: (name: string) => void
  onAssigneeSearch: (name: string) => void
  onBrowseCategory: (label: string) => void
}

/**
 * #4612/#4646: the Connections tab on a patent record. Reuses ConnectionsGraph
 * + ConnectionsLegend (via the additive fetchExplore/fetchDiscover and
 * nodeTypeKeys/edgeTypeKeys props built for this story) so it looks and
 * behaves exactly like Project/Document Connections, centred on a patent
 * instead of an invention document.
 */
export default function PatentConnectionsPanel({
  patentId, onOpenPatent, onInventorSearch, onAssigneeSearch, onBrowseCategory,
}: PatentConnectionsPanelProps) {
  const [selectedNode, setSelectedNode] = useState<ConnectionsNode | null>(null)
  const [hiddenNodeTypes, setHiddenNodeTypes] = useState<Set<string>>(new Set())
  const [hiddenEdgeTypes, setHiddenEdgeTypes] = useState<Set<string>>(new Set())
  const [resetCounter, setResetCounter] = useState(0)

  const handleSpread = useCallback(() => {
    clearPositions(patentId)
    setResetCounter((c) => c + 1)
  }, [patentId])

  const toggleNodeType = useCallback((type: string) => {
    setHiddenNodeTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type); else next.add(type)
      return next
    })
  }, [])

  const toggleEdgeType = useCallback((type: string) => {
    setHiddenEdgeTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type); else next.add(type)
      return next
    })
  }, [])

  // Summary — dedupes with ConnectionsGraph's own query (same key + fetcher),
  // so no extra network call. The summary text itself is server-computed
  // (Stream 1) from the SAME data as the graph, so there's one source of truth.
  const { data: graphData } = useQuery({
    queryKey: ['patent-connections', patentId],
    queryFn: () => getPatentConnections(patentId),
    enabled: !!patentId,
  })

  const summaryLine = useMemo(() => {
    const s = graphData?.summary
    if (!s) return null
    const parts: string[] = []
    if (s.similar_count > 0) parts.push(`${s.similar_count} similar patent${s.similar_count === 1 ? '' : 's'}`)
    if (s.inventor_count > 0) parts.push(`${s.inventor_count} inventor${s.inventor_count === 1 ? '' : 's'}`)
    if (s.assignee_count > 0) parts.push(`${s.assignee_count} assignee${s.assignee_count === 1 ? '' : 's'}`)
    if (s.family_count > 0) parts.push(`${s.family_count} family member${s.family_count === 1 ? '' : 's'}`)
    if (parts.length === 0) return null
    let text = `This patent connects to ${parts.join(', ')}.`
    if (s.top_similar.length > 0) text += ` Closest: ${s.top_similar.join(', ')}.`
    return text
  }, [graphData])

  const fetchExplore = useCallback(() => getPatentConnections(patentId), [patentId])
  const fetchDiscover = useCallback(
    (nodeId: string, nodeType: string) => discoverPatentConnections(patentId, nodeId, nodeType),
    [patentId],
  )

  return (
    <div className="light-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-sky-200/30">
        <h3 className="text-sm font-semibold text-gray-700">Connections</h3>
        <button
          onClick={handleSpread}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded-md hover:bg-white/30"
          title="Re-run layout — spreads dense clusters out so labels don't overlap"
        >
          <Maximize size={14} /> Spread
        </button>
      </div>

      {summaryLine && (
        <div className="px-4 py-3 bg-iflow-50 border-b border-iflow-200 text-sm text-gray-700" data-testid="patent-connections-summary">
          {summaryLine}
        </div>
      )}

      <div className="flex w-full max-w-full overflow-hidden" style={{ minHeight: 420, maxHeight: 'calc(100vh - 300px)' }}>
        <div className="flex-1 relative" style={{ minHeight: 420 }}>
          <ConnectionsGraph
            documentId={patentId}
            layout="cose"
            onNodeSelect={setSelectedNode}
            hiddenNodeTypes={hiddenNodeTypes}
            hiddenEdgeTypes={hiddenEdgeTypes}
            resetCounter={resetCounter}
            fetchExplore={fetchExplore}
            fetchDiscover={fetchDiscover}
          />
        </div>

        <div className="w-32 border-l border-sky-200/30 px-2.5 py-2 flex-shrink-0 overflow-y-auto bg-white/60">
          <ConnectionsLegend
            hiddenNodeTypes={hiddenNodeTypes}
            hiddenEdgeTypes={hiddenEdgeTypes}
            onToggleNodeType={toggleNodeType}
            onToggleEdgeType={toggleEdgeType}
            nodeTypeKeys={PATENT_NODE_TYPE_KEYS}
            edgeTypeKeys={PATENT_EDGE_TYPE_KEYS}
          />
        </div>

        {selectedNode && (
          <div className="w-56 border-l border-sky-200/30 p-3 flex-shrink-0 overflow-y-auto">
            <PatentNodeDetailPanel
              node={selectedNode}
              onClose={() => setSelectedNode(null)}
              onOpenPatent={onOpenPatent}
              onInventorSearch={onInventorSearch}
              onAssigneeSearch={onAssigneeSearch}
              onBrowseCategory={onBrowseCategory}
            />
          </div>
        )}
      </div>
    </div>
  )
}
